// src/oura/components/search/KeywordCards.tsx
//
// The searches the site already appears for, straight from Search Console:
// the five that bring the most visits, and every one of them in a table.
//
// The backend sends at most 50 searches, sorted by how often the site was
// shown (KEYWORD_LIMIT in aeo_search.py), and Google withholds rare ones, so
// the list is a subset. The only total on the wire is the rank bands' count
// of searches for what the business sells, so only those rows are set
// against it ("Top 10 of 180" on What you sell), never the brand rows too.

import { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon, ChevronsUpDownIcon, ListIcon, SearchIcon } from "lucide-react";
import type { AeoSearchKeyword } from "@service/aeo";
import { cn } from "../../lib/cn";
import { ArrowLink, FOCUS_RING, FamilyChip, FoldCard, Seg, TYPE } from "@oura/ui";
import { EMPTY_CELL, TABLE } from "../client/staffScreen";
import { num, pct, position, positionTone } from "./format";
import { Change } from "./PctChange";

/**
 * A search, written out in full, with the Brand chip when it contains the
 * business's own name. It wraps rather than truncating: "nyonya table" and
 * "nyonya table bukit timah" both cut to "nyonya …" on a phone. Below 640px
 * the chip goes under the words so they keep the width.
 */
function Query({ keyword, isStaff }: { keyword: AeoSearchKeyword; isStaff?: boolean }) {
    return (
        <span className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className={cn(TYPE.tb, "min-w-0 whitespace-normal break-words text-gray-900")}>{keyword.query}</span>
            {keyword.branded && <FamilyChip family="s">{isStaff ? "Brand" : "Your name"}</FamilyChip>}
        </span>
    );
}

/**
 * Position movement for one search. NEGATIVE is an improvement (8 to 4
 * arrives as -4), so the arrow and the colour are deliberately inverted
 * against a clicks change. Getting this backwards would tell a client they
 * are sinking while they climb. Null is a search new this window: it has not
 * moved, it has appeared.
 */
function PositionDelta({ value }: { value: number | null }) {
    if (value === null || value === 0) return <span className="text-gray-300">{EMPTY_CELL}</span>;
    return (
        <span className={TYPE.tc}>
            <Change up={value < 0}>{Math.abs(value).toFixed(1)}</Change>
        </span>
    );
}

export function TopSearchesCard({
    keywords,
    days,
    isStaff,
    onShowAll,
}: {
    keywords: AeoSearchKeyword[];
    days?: number;
    isStaff?: boolean;
    /** Opens the full table below. */
    onShowAll: () => void;
}) {
    const top = useMemo(
        () => [...keywords].sort((a, b) => b.clicks - a.clicks || b.impressions - a.impressions).slice(0, 5),
        [keywords],
    );
    return (
        <FoldCard
            id="top-searches"
            title="Searches that bring visits"
            icon={{ icon: SearchIcon, area: "google" }}
            flush
            right={(
                <>
                    {days !== undefined && <span className="whitespace-nowrap">{days} days</span>}
                    {keywords.length > top.length && <ArrowLink onClick={onShowAll}>See all {keywords.length}</ArrowLink>}
                </>
            )}
            foldedFact={top[0] ? `Most visits: ${top[0].query}` : undefined}
        >
            {top.length === 0 ? (
                <p className={cn(TYPE.tb, "m-0 px-5 pb-5 text-gray-500")}>No searches for this period.</p>
            ) : (
                <table className="w-full table-fixed border-collapse">
                    <colgroup>
                        <col />
                        <col className="w-16 sm:w-20" />
                        <col className="w-20 sm:w-24" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className={cn(TABLE.th, "text-left")}>Top 5 by visits</th>
                            <th className={cn(TABLE.th, "text-right")}>Visits</th>
                            <th className={cn(TABLE.th, "text-right")}>Position</th>
                        </tr>
                    </thead>
                    <tbody>
                        {top.map((k) => (
                            <tr key={k.query}>
                                <td className={cn(TABLE.td, "py-2.5")}>
                                    <Query keyword={k} isStaff={isStaff} />
                                </td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-right font-medium tabular-nums text-gray-900")}>{num(k.clicks)}</td>
                                <td className={cn(TABLE.td, TYPE.tc, "text-right tabular-nums text-gray-500")}>{position(k.position)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </FoldCard>
    );
}

type SortKey = "impressions" | "clicks" | "ctr" | "position";
type Group = "all" | "nonBranded" | "branded";

const COLUMNS: Array<{ key: SortKey; label: string; clientLabel?: string }> = [
    { key: "impressions", label: "Times shown" },
    { key: "clicks", label: "Visits" },
    { key: "ctr", label: "Click rate", clientLabel: "% who visit" },
    { key: "position", label: "Position" },
];

/** Every search Search Console listed, sortable, with the brand split as a filter. */
export function AllSearchesCard({
    keywords,
    total,
    isStaff,
}: {
    keywords: AeoSearchKeyword[];
    isStaff?: boolean;
    /**
     * The one total on the wire: the rank bands' count of searches for what
     * the business sells (`searchesForWhatYouSell`). Null, the head counts
     * the rows and claims no total.
     */
    total?: number | null;
}) {
    const [sort, setSort] = useState<SortKey>("impressions");
    const [dir, setDir] = useState<"asc" | "desc">("desc");
    const [group, setGroup] = useState<Group>("all");

    const rows = useMemo(() => {
        const filtered = group === "all" ? keywords : keywords.filter((k) => (group === "branded" ? k.branded : !k.branded));
        return [...filtered].sort((a, b) => (dir === "asc" ? a[sort] - b[sort] : b[sort] - a[sort]));
    }, [keywords, sort, dir, group]);

    const toggle = (key: SortKey) => {
        if (sort === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
        // Position sorts ascending by default (1 is best); every other metric
        // sorts descending (more is better).
        else {
            setSort(key);
            setDir(key === "position" ? "asc" : "desc");
        }
    };
    const branded = keywords.filter((k) => k.branded).length;
    const sells = keywords.length - branded;
    // `total` counts searches for what the business sells ONLY (the rank
    // bands are non-branded), so it is set against those rows alone. "Top 12
    // of 180" put the two searches for the business's own name inside a
    // total that leaves them out, and under the Where you rank card's "180
    // searches for what you sell" the same 180 read as two different counts.
    const ofTotal = typeof total === "number" && total >= sells && sells > 0 ? total : null;
    const plural = (n: number) => `${n.toLocaleString("en-GB")} ${n === 1 ? "search" : "searches"}`;
    const count = ofTotal !== null && group === "nonBranded"
        ? `Top ${sells} of ${ofTotal.toLocaleString("en-GB")}`
        : plural(rows.length);
    const filter = (className?: string) => (
        <Seg<Group>
            size="sm"
            ariaLabel="Show searches for"
            value={group}
            onChange={setGroup}
            className={className}
            items={[
                { key: "all", label: "All", count: keywords.length },
                { key: "nonBranded", label: "What you sell", count: keywords.length - branded },
                { key: "branded", label: "Your name", count: branded },
            ]}
        />
    );

    return (
        <FoldCard
            id="all-searches"
            anchor="all-searches"
            defaultFolded
            title={isStaff ? "Searches Search Console lists" : "Searches you show up for"}
            icon={{ icon: ListIcon, area: "google" }}
            flush
            right={(
                <>
                    <span className="whitespace-nowrap tabular-nums">{count}</span>
                    {/* Beside the title from 640px. Below it the three
                        filters do not fit a phone's head, so they sit in the
                        body, where they can wrap. */}
                    <span className="hidden sm:block">{filter()}</span>
                </>
            )}
        >
            <div className="px-5 pb-3 sm:hidden">{filter("flex-wrap")}</div>
            <p className={cn(TYPE.tc, "m-0 px-5 pb-3 text-gray-500")}>
                {ofTotal !== null && (
                    <>
                        The top <span className="tabular-nums">{sells}</span> of the{" "}
                        <span className="tabular-nums">{ofTotal.toLocaleString("en-GB")}</span> searches for what you sell, and{" "}
                        <span className="tabular-nums">{branded}</span> for your name.{" "}
                    </>
                )}
                {isStaff
                    ? "Straight from Search Console: searches the site already shows up for, not opportunities. Google leaves out the rarest ones."
                    : "Searches Google already shows your site for, as Google reports them. Google leaves out the rarest ones."}
            </p>
            <div className={TABLE.scroll}>
                <table className="w-full min-w-[40rem] border-collapse">
                    <thead>
                        <tr>
                            <th className={cn(TABLE.th, "text-left")}>Search</th>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    aria-sort={sort === col.key ? (dir === "asc" ? "ascending" : "descending") : "none"}
                                    className={cn(TABLE.th, "w-px text-right")}
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggle(col.key)}
                                        className={cn("inline-flex items-center gap-1 rounded hover:text-gray-900", sort === col.key && "text-blue-700", FOCUS_RING)}
                                    >
                                        {isStaff ? col.label : col.clientLabel ?? col.label}
                                        {sort !== col.key
                                            ? <ChevronsUpDownIcon aria-hidden size={12} className="opacity-40" />
                                            : dir === "asc" ? <ChevronUpIcon aria-hidden size={12} /> : <ChevronDownIcon aria-hidden size={12} />}
                                    </button>
                                </th>
                            ))}
                            <th className={cn(TABLE.th, "w-px text-right")}>Change</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={6} className={cn(TABLE.td, TYPE.tb, "py-10 text-center text-gray-500")}>
                                    No searches in this group for the period.
                                </td>
                            </tr>
                        ) : (
                            rows.map((k) => (
                                <tr key={k.query} className="transition-colors hover:bg-gray-50">
                                    <td className={cn(TABLE.td, "py-2.5")}>
                                        <Query keyword={k} isStaff={isStaff} />
                                    </td>
                                    <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums text-gray-700")}>{num(k.impressions)}</td>
                                    <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums text-gray-700")}>{num(k.clicks)}</td>
                                    <td className={cn(TABLE.td, TYPE.tc, "text-right tabular-nums text-gray-500")}>{pct(k.ctr)}</td>
                                    <td className={cn(TABLE.td, TYPE.tb, "text-right font-medium tabular-nums", positionTone(k.position))}>{position(k.position)}</td>
                                    <td className={cn(TABLE.td, "text-right")}><PositionDelta value={k.positionDelta} /></td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </FoldCard>
    );
}
